import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapPin, RefreshCw } from 'lucide-react';
import { stcBinService } from '../../../../../services/stcBinService';
import { StcBinPagedDto, StcBinsPagedQueryParams } from '../../../../../types/stcBin';
import { BackButton } from '../../../../common/BackButton';
import { WarehouseTable } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, ServerFetchParams } from '../../../../shared/table/types/table';

interface StcBinsViewerProps {
    onBack: () => void;
}

// Extend with id field required by WarehouseTable
type StcBinRow = StcBinPagedDto & { id: number };

export const StcBinsViewer: React.FC<StcBinsViewerProps> = ({ onBack }) => {
    // Server-side pagination state
    const [serverBins, setServerBins] = useState<StcBinRow[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    // UI state
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Guard against multiple simultaneous calls
    const isLoadingRef = useRef(false);
    // Store latest fetch params for refresh
    const latestFetchParamsRef = useRef<StcBinsPagedQueryParams>({ pageNumber: 1, pageSize: 50 });
    // Debounce timer for column filters
    const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const columnFactory = createColumnFactory<StcBinRow>();

    // Fetch paged bins from server
    const fetchPagedBins = useCallback(async (params: StcBinsPagedQueryParams) => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;

        setIsLoading(true);
        setError(null);

        try {
            const result = await stcBinService.getCurrentPaged(params);

            const rows: StcBinRow[] = result.items.map((bin) => ({
                ...bin,
                id: bin.id,
            }));

            setServerBins(rows);
            setTotalRecords(result.totalRecords);
            latestFetchParamsRef.current = params;
        } catch (err) {
            console.error('Error loading paged bins:', err);
            setError('Kunne ikke laste lokasjonsdata. Prøv igjen.');
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, []);

    // Server-side pagination callback — called by WarehouseTable when page/sort/filter changes
    const handleServerFetch = useCallback((params: ServerFetchParams) => {
        const f = params.filters;
        const queryParams: StcBinsPagedQueryParams = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            sortColumn: params.sortColumn,
            sortDirection: params.sortDirection,
            binCode: f?.binCode || undefined,
            description1: f?.description1 || undefined,
            warehouseID: f?.warehouseID || undefined,
            stcZoneID: f?.stcZoneID || undefined,
            binGrpNo: f?.binGrpNo || undefined,
            stcBinCatNo: f?.stcBinCatNo || undefined,
            binTypeNo: f?.binTypeNo || undefined,
            binSizeNo: f?.binSizeNo || undefined,
            movementTypeNo: f?.movementTypeNo || undefined,
            resourceNo: f?.resourceNo || undefined,
            pickPriorityNo: f?.pickPriorityNo || undefined,
            putAwayPriorityNo: f?.putAwayPriorityNo || undefined,
            customSortID: f?.customSortID || undefined,
            replenishMinLevel: f?.replenishMinLevel || undefined,
            replenishMaxLevel: f?.replenishMaxLevel || undefined,
            sharedBinMaxQuantity: f?.sharedBinMaxQuantity || undefined,
            pickCustomOrder: f?.pickCustomOrder || undefined,
            putAwayCustomOrder: f?.putAwayCustomOrder || undefined,
            itemNo: f?.itemNo || undefined,
            pickableYesNo: f?.pickableYesNo || undefined,
            reservationAllowed: f?.reservationAllowed || undefined,
            autoReplenishment: f?.autoReplenishment || undefined,
            negativeReservationAllowed: f?.negativeReservationAllowed || undefined,
            sharedBinYesNo: f?.sharedBinYesNo || undefined,
            sharedBinPreferEmptyBin: f?.sharedBinPreferEmptyBin || undefined,
            lastCountedDate: f?.lastCountedDate || undefined,
            lastMovementDate: f?.lastMovementDate || undefined,
        };

        // Debounce filter changes (300ms) but execute page/sort changes immediately
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }

        const prevFilters = latestFetchParamsRef.current;
        const filterKeys: (keyof StcBinsPagedQueryParams)[] = [
            'binCode', 'description1', 'warehouseID', 'stcZoneID',
            'binGrpNo', 'stcBinCatNo', 'binTypeNo', 'binSizeNo',
            'movementTypeNo', 'resourceNo', 'pickPriorityNo', 'putAwayPriorityNo',
            'customSortID', 'replenishMinLevel', 'replenishMaxLevel', 'sharedBinMaxQuantity',
            'pickCustomOrder', 'putAwayCustomOrder', 'itemNo',
            'pickableYesNo', 'reservationAllowed', 'autoReplenishment',
            'negativeReservationAllowed', 'sharedBinYesNo', 'sharedBinPreferEmptyBin',
            'lastCountedDate', 'lastMovementDate',
        ];
        const isFilterChange = params.pageNumber === 1 &&
            filterKeys.some(key => queryParams[key] !== prevFilters[key]);

        if (isFilterChange) {
            filterDebounceRef.current = setTimeout(() => {
                fetchPagedBins(queryParams);
            }, 300);
        } else {
            fetchPagedBins(queryParams);
        }
    }, [fetchPagedBins]);

    // Refresh helper
    const refreshData = useCallback(() => {
        fetchPagedBins(latestFetchParamsRef.current);
    }, [fetchPagedBins]);

    // Initial load
    useEffect(() => {
        fetchPagedBins({ pageNumber: 1, pageSize: 50 });
    }, [fetchPagedBins]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
            }
        };
    }, []);

    // Column definitions
    const columns: ColumnDefinition<StcBinRow>[] = useMemo(() => [
        columnFactory.createTextColumn('binCode', 'binCode', 'Lokasjon', { width: 140, sortable: true, filterType: 'text' }),
        columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', { width: 200, sortable: true, filterType: 'text' }),
        columnFactory.createTextColumn('warehouseID', 'warehouseID', 'Lager', { width: 100, sortable: true, filterType: 'text' }),
        columnFactory.createTextColumn('stcZoneID', 'stcZoneID', 'Sone', { width: 100, sortable: true, filterType: 'text' }),
        {
            ...columnFactory.createTextColumn('zoneDescription', 'zoneDescription', 'Sonebeskrivelse', { width: 150, sortable: false }),
            visible: false,
        },
        {
            ...columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', 'Type nr', { width: 80, sortable: true }),
            visible: false,
        },
        columnFactory.createTextColumn('binTypeDescription', 'binTypeDescription', 'Type', { width: 130, sortable: false }),
        {
            ...columnFactory.createNumberColumn('binSizeNo', 'binSizeNo', 'Størrelse nr', { width: 90, sortable: true }),
            visible: false,
        },
        columnFactory.createTextColumn('binSizeDescription', 'binSizeDescription', 'Størrelse', { width: 120, sortable: false }),
        {
            ...columnFactory.createNumberColumn('stcBinCatNo', 'stcBinCatNo', 'Kategori nr', { width: 90, sortable: true }),
            visible: false,
        },
        columnFactory.createTextColumn('binCategoryDescription', 'binCategoryDescription', 'Kategori', { width: 130, sortable: false }),
        {
            ...columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', 'Gruppe nr', { width: 90, sortable: true }),
            visible: false,
        },
        columnFactory.createTextColumn('binGrpDescription', 'binGrpDescription', 'Gruppe', { width: 130, sortable: false }),
        columnFactory.createBooleanColumn('pickableYesNo', 'pickableYesNo', 'Plukk', { width: 70, sortable: true }),
        columnFactory.createBooleanColumn('reservationAllowed', 'reservationAllowed', 'Reserv.', { width: 70, sortable: false }),
        columnFactory.createBooleanColumn('sharedBinYesNo', 'sharedBinYesNo', 'Delt', { width: 70, sortable: false }),
        columnFactory.createBooleanColumn('autoReplenishment', 'autoReplenishment', 'Auto-etterfyll', { width: 100, sortable: false }),
        columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr', { width: 120, sortable: true }),
        columnFactory.createNumberColumn('pickPriorityNo', 'pickPriorityNo', 'Plukkpri.', { width: 80, sortable: true }),
        columnFactory.createNumberColumn('customSortID', 'customSortID', 'Sortering', { width: 80, sortable: true }),
        {
            ...columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', 'Siste bevegelse', { width: 130, sortable: true }),
            visible: false,
        },
        {
            ...columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', 'Siste telling', { width: 130, sortable: true }),
            visible: false,
        },
        {
            ...columnFactory.createDateColumn('lastUpdate', 'lastUpdate', 'Oppdatert', { width: 130, sortable: true }),
            visible: false,
        },
    ], [columnFactory]);

    // Table actions
    const tableActions = useMemo(() => [
        {
            id: 'refresh',
            label: 'Oppdater',
            icon: RefreshCw,
            onClick: () => refreshData(),
            variant: 'secondary' as const
        }
    ], [refreshData]);

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 px-2">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    <h1 className="text-xl font-semibold">Lokasjoner</h1>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 min-h-0">
                <WarehouseTable
                    data={serverBins}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: false,
                        showFilters: true,
                        showExport: false,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'stcbins-data-table'
                    }}
                    serverPagination={{
                        enabled: true,
                        totalRecords: totalRecords,
                        onFetchData: handleServerFetch
                    }}
                    tableActions={tableActions}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>
        </div>
    );
};
